(() => {
  'use strict';
  const normalizeFormat = title => String(title || '').toLowerCase().replace(/[″"”]/g, '').replace(/×/g, 'x').replace(/\s+/g, '');
  const available = book => (book?.variants || []).filter(v => v.available === true && v.local === true && Number.isSafeInteger(v.priceCents) && v.priceCents > 0);
  function priceSet(variants) {
    if (variants.length !== 3 || variants.some(v => !v || !Number.isSafeInteger(v.priceCents) || v.priceCents <= 0)) return null;
    const prices = variants.map(v => v.priceCents), saving = Math.min(...prices);
    return { subtotal: prices.reduce((a, b) => a + b, 0), saving, total: prices.reduce((a, b) => a + b, 0) - saving, freeIndex: prices.lastIndexOf(saving) };
  }
  const commonFormats = books => available(books[0]).filter(v => books.every(book => available(book).some(other => normalizeFormat(other.title) === normalizeFormat(v.title))));
  function matchingAddedSelection(detail, selected, itemIds) {
    if (detail?.demo === true || !Array.isArray(detail?.addedProductIds) || !Array.isArray(itemIds) || itemIds.length !== 3 || !selected.every(book => detail.addedProductIds.map(String).includes(book.id))) return null;
    const actual = selected.map((book, index) => (detail.items || []).find(item => item.itemId === itemIds[index] && String(item.productId) === book.id));
    return actual.every(Boolean) ? actual : null;
  }
  function rankBooks(books, current) {
    const ages = current.tags.filter(t => t.startsWith('age:'));
    const currentGender = current.tags.find(t => t === 'gender:boy' || t === 'gender:girl');
    return books.filter(book => book.id !== current.id && book.personCount === 1 && available(book).length && book.image && (!ages.length || book.tags.some(tag => ages.includes(tag))) && (!currentGender || !book.tags.some(tag => tag.startsWith('gender:') && tag !== currentGender))).map((book, order) => {
      let score = 0;
      for (const tag of book.tags) if (current.tags.includes(tag)) score += tag.startsWith('pf:taxon:interest-') ? 10 : tag.startsWith('theme:') ? 6 : tag.startsWith('age:') ? 2 : 0;
      if (current.id === '15233425604974') {
        if (book.id === '15233423901038') score += 100;
        if (book.id === '15406742634862') score += 90;
      }
      return { ...book, score, order };
    }).sort((a, b) => b.score - a.score || a.order - b.order);
  }
  if (typeof module === 'object' && module.exports) module.exports = { normalizeFormat, available, priceSet, commonFormats, rankBooks, matchingAddedSelection };
  if (typeof document === 'undefined') return;
  const make = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
  const parse = (value, fallback) => { try { return JSON.parse(value); } catch (_) { return fallback; } };
  function init(root) {
    if (root.dataset.libraryReady || Date.now() >= Date.parse(root.dataset.end)) return;
    const source = document.querySelector('[data-story-book-options]')?.content;
    if (!source || !window.WonderbookPhotoOnboarding?.openSelection) return;
    const entities = { '&amp;': '&', '&quot;': '"', '&#39;': "'", '&#x27;': "'", '&apos;': "'", '&lt;': '<', '&gt;': '>' };
    const strings = Object.fromEntries(Object.entries(parse(root.dataset.strings, {})).map(([key, value]) => [key, String(value).replace(/&(?:amp|quot|#39|#x27|apos|lt|gt);/gi, entity => entities[entity.toLowerCase()])])), eligible = new Set(parse(root.dataset.eligibleIds, []).map(String));
    const books = [], seen = new Set();
    source.querySelectorAll('[data-story-book]').forEach(node => {
      const id = node.dataset.shopifyProductId;
      if (!eligible.has(id) || seen.has(id)) return;
      const variants = parse(node.dataset.purchase, {}).variants || [];
      let image = node.querySelector('img')?.dataset.src || '';
      try { const u = new URL(image, location.origin); if (u.protocol !== 'https:') return; u.searchParams.set('width', '624'); image = u.href; } catch (_) { return; }
      const book = { id, title: node.dataset.title, image, variants, tags: (node.dataset.terms || '').split(/\s+/), personCount: Number(node.dataset.personCount) };
      if (!available(book).length) return;
      seen.add(id); books.push(book);
    });
    const current = books.find(book => book.id === root.dataset.currentProduct);
    if (!current || current.personCount !== 1) return;
    current.title = root.dataset.currentTitle || current.title;
    const ranked = rankBooks(books, current);
    if (ranked.length < 2) return;
    let selected = [current, ranked[0], ranked[1]], variants = selected.map(book => available(book).slice().sort((a, b) => a.priceCents - b.priceCents)[0]);
    let same = true, busy = false, version = 0, swapOpener = null, submitted = false, attempted = false, cartPending = false, activeItemIds = null, activeSelection = null;
    const find = name => root.querySelector(`[data-library-${name}]`);
    const grid = find('books'), status = find('status'), mode = find('format-mode'), shared = find('shared-format'), dialog = find('dialog');
    let formatter;
    try { formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: root.dataset.currency }); } catch (_) { return; }
    const money = cents => formatter.format(cents / 100);
    const text = (key, fallback = '') => strings[key] || fallback;
    const announce = message => { status.textContent = message; };
    const optIn = find('opt-in'), bundleControls = find('bundle-controls');
    const form = root.closest?.('form'), product = root.closest?.('[data-product-template]');
    const singleStart = form?.querySelector('[data-sticky-source-atc]');
    const start = singleStart || find('start');
    if (!start || singleStart?.disabled) return;
    const startLabel = start.querySelector('[data-product-cta-label]') || start;
    const singleLabel = startLabel.textContent;
    const singlePrice = form?.querySelector('.wp-product-template__price-line--above-cta');
    const floatingStart = product?.querySelector('[data-avatar-first-preview-open]');
    const floatingLabel = floatingStart?.querySelector('[data-product-cta-label]');
    const singleFloatingLabel = floatingLabel?.textContent || '';
    let bundleSelected = optIn ? optIn.checked : true;
    function syncPurchaseChoice() {
      const enabled = bundleSelected && !root.hidden && Date.now() < Date.parse(root.dataset.end);
      if (bundleControls) bundleControls.hidden = !bundleSelected;
      if (singlePrice) singlePrice.hidden = enabled;
      if (form) form.dataset.libraryBundleActive = String(enabled);
      const label = busy || cartPending ? text('loading') : attempted || submitted ? text('continuePersonalizing') : text('chooseStories');
      if (singleStart) { startLabel.textContent = enabled ? label : singleLabel; start.disabled = enabled && (busy || cartPending || !priceSet(variants)); }
      if (floatingLabel) floatingLabel.textContent = enabled ? label : singleFloatingLabel;
      if (floatingStart) {
        floatingStart.setAttribute('aria-disabled', String(enabled && (busy || cartPending)));
      }
      if (optIn) { optIn.checked = bundleSelected; optIn.disabled = busy || cartPending || attempted || submitted; }
    }
    function selectBundle() { bundleSelected = true; syncPurchaseChoice(); }
    optIn?.addEventListener('change', () => { bundleSelected = optIn.checked; announce(''); syncPurchaseChoice(); });
    document.addEventListener('click', event => {
      if (!bundleSelected || root.hidden || ![singleStart, floatingStart].some(button => button?.contains(event.target))) return;
      if (Date.now() >= Date.parse(root.dataset.end)) { root.hidden = true; syncPurchaseChoice(); return; }
      event.preventDefault(); event.stopImmediatePropagation();
      if (!busy && !cartPending) void openLibrary();
    }, true);
    document.addEventListener('submit', event => {
      if (!bundleSelected || root.hidden || !form || event.target !== form) return;
      if (Date.now() >= Date.parse(root.dataset.end)) { root.hidden = true; syncPurchaseChoice(); return; }
      event.preventDefault(); event.stopImmediatePropagation();
      if (!busy && !cartPending) void openLibrary();
    }, true);
    window.addEventListener('wonderbook:product-actions-updated', syncPurchaseChoice);
    const titleFor = (label, book) => `${label}: ${book.title}`;
    const image = book => { const img = make('img', 'wp-library__image'); img.src = book.image; img.alt = book.title; img.width = 156; img.height = 156; img.loading = 'lazy'; img.decoding = 'async'; return img; };
    function formatButton(variant, key, chosen, price, choose) {
      const button = make('button', 'wp-library__format-card'); button.type = 'button';
      button.dataset.libraryFocus = key; button.setAttribute('aria-pressed', String(chosen));
      button.setAttribute('aria-label', `${variant.title} · ${price} ${text('each')}`);
      button.disabled = busy || cartPending || attempted || submitted;
      const match = String(variant.title).match(/(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)[\s″"”]*/i);
      const size = match ? `${match[1]} × ${match[2]}″` : variant.title;
      const binding = match ? variant.title.replace(match[0], '').trim() : '';
      if (!match) button.classList.add('wp-library__format-card--named');
      const art = make('span', 'wp-library__format-art'); art.setAttribute('aria-hidden', 'true');
      const silhouette = make('span', 'wp-library__format-book');
      silhouette.style.setProperty('--book-size', `${match ? Math.min(44, Math.max(26, Number(match[1]) * 4)) : 36}px`);
      silhouette.style.setProperty('--book-height', `${match ? Math.min(44, Math.max(26, Number(match[2]) * 4)) : 36}px`);
      if (/hard/i.test(binding)) silhouette.classList.add('wp-library__format-book--hard');
      art.append(silhouette);
      const detail = make('span', 'wp-library__format-detail');
      detail.append(make('span', 'wp-library__format-size', size));
      if (binding) detail.append(make('span', 'wp-library__format-binding', binding));
      detail.append(make('span', 'wp-library__format-price', `${price} ${text('each')}`));
      const tick = make('span', 'wp-library__format-tick', '✓'); tick.setAttribute('aria-hidden', 'true');
      button.append(art, detail, tick); button.addEventListener('click', choose);
      return button;
    }
    function setShared(key) {
      const matches = selected.map(book => available(book).find(v => normalizeFormat(v.title) === key));
      if (matches.every(Boolean)) { variants = matches; same = true; return true; }
      return false;
    }
    const common = commonFormats(selected);
    if (!common.length || !setShared(normalizeFormat(variants[0].title))) same = false;
    function render() {
      const focused = document.activeElement?.dataset.libraryFocus;
      const total = priceSet(variants);
      grid.replaceChildren();
      const mixed = find('mixed-formats'); mixed.replaceChildren(); mixed.hidden = same;
      selected.forEach((book, index) => {
        const card = make('article', 'wp-library__card');
        const cover = make(index > 0 ? 'button' : 'div', 'wp-library__cover'); cover.append(image(book));
        if (index > 0) {
          cover.type = 'button'; cover.dataset.libraryFocus = `swap-${index}`;
          cover.setAttribute('aria-label', titleFor(text('changeStory'), book));
          cover.disabled = busy || cartPending || attempted || submitted;
          const glyph = make('span', 'wp-library__swap-glyph', '↻'); glyph.setAttribute('aria-hidden', 'true'); cover.append(glyph);
          cover.addEventListener('click', () => openSwap(index, cover));
        }
        if (total?.freeIndex === index) {
          const badge = make('span', 'wp-library__free', text('free'));
          badge.setAttribute('aria-label', `${text('freeWhenEligible')}: ${book.title} · ${money(variants[index].priceCents)}`);
          cover.append(badge);
        }
        card.append(cover);
        if (!same) {
          const group = make('div', 'wp-library__individual-format'); group.setAttribute('role', 'group');
          group.setAttribute('aria-label', titleFor(text('format'), book));
          const identity = make('div', 'wp-library__format-identity'); identity.append(image(book));
          const options = make('div', 'wp-library__format-options');
          available(book).forEach(v => options.append(formatButton(v, `format-${index}-${v.id}`, String(v.id) === String(variants[index].id), money(v.priceCents), () => { variants[index] = v; announce(''); render(); })));
          group.append(identity, options); mixed.append(group);
        }
        grid.append(card);
      });
      const formats = commonFormats(selected); shared.replaceChildren();
      formats.forEach(v => {
        const key = normalizeFormat(v.title), prices = selected.map(book => available(book).find(other => normalizeFormat(other.title) === key).priceCents);
        const low = Math.min(...prices), high = Math.max(...prices), price = low === high ? money(low) : `${money(low)}–${money(high)}`;
        shared.append(formatButton(v, `shared-${key}`, key === normalizeFormat(variants[0].title), price, () => { setShared(key); announce(''); render(); }));
      });
      mode.textContent = same ? text('chooseIndividually') : text('useSameFormat');
      mode.setAttribute('aria-expanded', String(!same)); mode.disabled = busy || cartPending || attempted || submitted || (!same && !formats.length);
      find('format-note').hidden = !same;
      find('shared-format-wrap').hidden = !same;
      find('format-summary').textContent = variants.every(v => normalizeFormat(v.title) === normalizeFormat(variants[0].title)) ? variants[0].title : text('mixedSizes');
      find('total').textContent = total ? money(total.total) : '—';
      find('subtotal').textContent = total ? money(total.subtotal) : '';
      find('subtotal').setAttribute('aria-label', total ? `${text('beforeOffer')} ${money(total.subtotal)}` : '');
      find('savings').textContent = total ? `${text('savings')} ${money(total.saving)}` : ''; find('savings').hidden = !total;
      if (!singleStart) { start.disabled = busy || cartPending || !total; startLabel.textContent = busy || cartPending ? text('loading') : attempted || submitted ? text('continuePersonalizing') : text('chooseStories'); }
      syncPurchaseChoice();
      if (focused) [...root.querySelectorAll('[data-library-focus]')].find(node => node.dataset.libraryFocus === focused)?.focus({ preventScroll: true });
    }
    function openSwap(index, trigger) {
      if (busy || cartPending || attempted || submitted) return;
      selectBundle();
      swapOpener = trigger;
      const choices = find('choices'); choices.replaceChildren();
      const candidates = ranked.filter(book => !selected.some((chosen, i) => i !== index && chosen.id === book.id));
      candidates.forEach(book => {
        const button = make('button', 'wp-library__choice'); button.type = 'button';
        button.append(image(book)); button.setAttribute('aria-label', titleFor(text('chooseBook'), book));
        button.setAttribute('aria-pressed', String(book.id === selected[index].id));
        button.addEventListener('click', () => {
          const key = normalizeFormat(variants[index].title); selected[index] = book;
          variants[index] = available(book).find(v => normalizeFormat(v.title) === key) || available(book)[0];
          if (same && !setShared(normalizeFormat(variants[0].title))) same = false;
          dialog.close(); render(); announce(text('ready'));
          root.querySelector(`[data-library-focus="swap-${index}"]`)?.focus({ preventScroll: true });
        });
        choices.append(button);
      });
      if (!candidates.length) choices.append(make('p', '', text('noMatches')));
      dialog.showModal(); find('close').focus({ preventScroll: true });
    }
    find('close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => { swapOpener?.isConnected && swapOpener.focus({ preventScroll: true }); });
    mode.dataset.libraryFocus = 'mode';
    mode.addEventListener('click', () => { same = !same; if (same) { const formats = commonFormats(selected); if (!setShared(normalizeFormat(variants[0].title))) setShared(normalizeFormat(formats[0]?.title)); } announce(''); render(); });
    async function openLibrary() {
      if (busy || cartPending || Date.now() >= Date.parse(root.dataset.end)) { if (Date.now() >= Date.parse(root.dataset.end)) { root.hidden = true; syncPurchaseChoice(); } return; }
      const attempt = ++version; busy = true; announce(''); render();
      try {
        // The original selection identifies the saved journey. Editions changed
        // during personalization are already retained in that journey's drafts.
        const selection = (attempted || submitted) && activeSelection ? activeSelection : selected.map((book, index) => ({ productId: book.id, variantId: String(variants[index].id) }));
        const result = await window.WonderbookPhotoOnboarding.openSelection(start, selection, { returnToPreview: async () => { root.scrollIntoView({ block: 'nearest', behavior: 'auto' }); start.focus({ preventScroll: true }); return { opened: true }; } });
        if (attempt === version && result?.opened) { activeItemIds = result.itemIds; activeSelection = selection; }
        if (attempt === version && !result?.opened) announce(text('error'));
      } catch (error) { if (attempt === version) announce(error?.message || text('error')); }
      finally { if (attempt === version) { busy = false; render(); } }
    }
    if (!singleStart) start.addEventListener('click', openLibrary);
    window.addEventListener('wonderbook:library-cart-state', event => {
      const ids = event.detail?.itemIds;
      if (!activeSelection || !Array.isArray(activeItemIds) || activeItemIds.length !== 3 || !Array.isArray(ids) || ids.length !== 3 || !activeItemIds.every(id => ids.includes(id))) return;
      if (event.detail.phase === 'pending') cartPending = true;
      else if (event.detail.phase === 'attempted') attempted = true;
      else if (event.detail.phase === 'settled') cartPending = false;
      else return;
      render();
    });
    window.addEventListener('wonderbook:photo-cart-updated', event => {
      const detail = event.detail;
      const actual = matchingAddedSelection(detail, selected, activeItemIds);
      if (!actual) return;
      actual.forEach((item, i) => { const match = available(selected[i]).find(v => String(v.id) === String(item.variantId)); if (match) variants[i] = match; });
      same = variants.every(v => normalizeFormat(v.title) === normalizeFormat(variants[0].title)); submitted = true; announce(text('added')); render();
    });
    window.addEventListener('wonderbook:account-changed', () => { version++; busy = false; submitted = false; attempted = false; cartPending = false; activeItemIds = null; activeSelection = null; announce(''); if (dialog.open) dialog.close(); render(); });
    document.addEventListener('visibilitychange', () => { if (Date.now() >= Date.parse(root.dataset.end)) { root.hidden = true; syncPurchaseChoice(); if (dialog.open) dialog.close(); } });
    root.dataset.libraryReady = 'true'; render(); root.hidden = false; syncPurchaseChoice();
  }
  const boot = () => document.querySelectorAll('[data-little-library]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
  window.addEventListener('load', boot, { once: true });
})();
